import { Progress } from "@/components/ui/progress";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { DeviceHealthScore, getHealthScoreColor, HealthTrend } from "@/utils/deviceHealth";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";

interface HealthScoreCardProps {
  healthScore: DeviceHealthScore;
  trend?: HealthTrend | null;
  isLoadingTrend?: boolean;
  compact?: boolean;
}

export const HealthScoreCard = ({ 
  healthScore, 
  trend,
  isLoadingTrend = false,
  compact = false 
}: HealthScoreCardProps) => {
  const scoreColor = getHealthScoreColor(healthScore.overall);
  
  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <div className="relative">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center text-sm font-bold text-white"
            style={{ backgroundColor: scoreColor }}
          >
            {healthScore.overall}
          </div>
          <Badge 
            variant="outline" 
            className="absolute -top-1 -right-1 text-xs"
          >
            {healthScore.grade}
          </Badge>
        </div>
        <div>
          <p className="text-sm font-medium">Health Score</p>
          <p className="text-xs text-muted-foreground">
            Updated {new Date(healthScore.lastUpdated).toLocaleDateString()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader className="pb-3">
        <GlassCardTitle className="text-base flex items-center justify-between">
          Device Health Score
          <Badge variant="outline">{healthScore.grade}</Badge>
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-lg font-bold text-white"
                style={{ backgroundColor: scoreColor }}
              >
                {healthScore.overall}
              </div>
            </div>
            <div className="flex-1">
              <Progress 
                value={healthScore.overall} 
                className="h-3"
                style={{
                  // @ts-ignore
                  '--progress-color': scoreColor
                } as React.CSSProperties}
              />
              <p className="text-sm text-muted-foreground mt-1">
                Overall health rating
              </p>
            </div>
          </div>
          
          {isLoadingTrend && (
            <div className="text-sm text-muted-foreground">
              Loading trend data...
            </div>
          )}
          {!isLoadingTrend && trend && (
            <div className="flex items-center gap-2 text-sm">
              {trend.trend === "up" && (
                <>
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  <span className="text-green-600">
                    +{trend.percentChange}% from {trend.daysCompared} days ago
                  </span>
                </>
              )}
              {trend.trend === "down" && (
                <>
                  <TrendingDown className="h-4 w-4 text-red-500" />
                  <span className="text-red-600">
                    {trend.percentChange}% from {trend.daysCompared} days ago
                  </span>
                </>
              )}
              {trend.trend === "stable" && (
                <>
                  <Minus className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-600">
                    Stable over last {trend.daysCompared} days
                  </span>
                </>
              )}
            </div>
          )}
          {!isLoadingTrend && !trend && (
            <div className="text-sm text-muted-foreground">
              No historical data available yet
            </div>
          )}
          
          <p className="text-xs text-muted-foreground">
            Last updated: {new Date(healthScore.lastUpdated).toLocaleString()}
          </p>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
};